import React, { useState, useEffect } from 'react';
import { Compass, ClipboardList, Package, Ship } from 'lucide-react';
import '../styles/CycleStatusWidget.css';

// Декоративные оригами изображения
const origamiCrab = new URL('../assets/decorations/origami-crab.webp', import.meta.url).href;
const origamiShrimp = new URL('../assets/decorations/origami-shrimp.webp', import.meta.url).href;
const origamiFish = new URL('../assets/decorations/origami-fish.webp', import.meta.url).href;

// 4 этапа жизненного цикла заказа — каждый со своим цветом
const MILESTONES = [
  {
    percent: 0, label: 'На добыче лучших даров моря', icon: Compass,
    color: { r: 16, g: 185, b: 129 } // emerald
  },
  {
    percent: 33, label: 'Принимаем ваши заказы', icon: ClipboardList,
    color: { r: 14, g: 165, b: 233 } // sky (brand)
  },
  {
    percent: 66, label: 'Снаряжаем улов', icon: Package,
    color: { r: 245, g: 158, b: 11 } // amber
  },
  {
    percent: 100, label: 'Рыба в пути к вам', icon: Ship,
    color: { r: 139, g: 92, b: 246 } // violet
  }
];

// Цвет этапа: активный = свой цвет, неактивный = серый
const getStageColor = (isActive, milestone) => {
  const { r, g, b } = milestone.color;
  if (isActive) {
    return {
      background: `linear-gradient(135deg, rgba(${r},${g},${b},0.95), rgba(${r},${g},${b},0.8))`,
      shadow: `0 4px 16px rgba(${r},${g},${b},0.5), inset 0 1px 0 rgba(255,255,255,0.4)`
    };
  }
  return {
    background: 'linear-gradient(135deg, rgba(156,163,175,0.7), rgba(107,114,128,0.75))',
    shadow: '0 4px 16px rgba(156,163,175,0.35), inset 0 1px 0 rgba(255,255,255,0.35)'
  };
};

/**
 * Виджет статуса цикла с glassmorphism дизайном
 *
 * Показывает:
 * - Название/номер цикла
 * - Статус (ОТКРЫТ/ЗАКРЫТ)
 * - Планируемую дату доставки
 * - Countdown до закрытия
 * - Мотивационное сообщение с целевым количеством участников
 */
const CycleStatusWidget = ({
  cycleNumber = null,
  cycleName = null, // Из API: "Масленичный разгуляй", "Первая нерка" и т.д.
  status = 'open', // 'open' | 'closed' | 'inactive'
  deliveryDate = null, // Date object или ISO string
  closeDate = null, // Дата закрытия цикла для countdown
  progress = 0, // Прогресс в процентах (0-100)
  participants = 0, // Количество участников
  targetParticipants = 0, // Целевое количество участников для старта (миграции 059-060)
  timerSentinelRef = null, // Ref для sentinel-элемента (IntersectionObserver в HomePage)
}) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0 });

  // Countdown до закрытия
  useEffect(() => {
    if (!closeDate) return;

    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const end = new Date(closeDate).getTime();
      const distance = end - now;

      if (distance < 0) {
        return { days: 0, hours: 0, minutes: 0 };
      }

      return {
        days: Math.floor(distance / (1000 * 60 * 60 * 24)),
        hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60))
      };
    };

    // Обновляем каждую минуту
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 60000);

    // Начальный расчёт
    setTimeLeft(calculateTimeLeft());

    return () => clearInterval(timer);
  }, [closeDate]);

  // Форматирование даты доставки
  const formatDeliveryDate = (date) => {
    if (!date) return 'Дата уточняется';
    const d = new Date(date);
    const weekday = d.toLocaleDateString('ru-RU', { weekday: 'long' });
    const day = d.getDate();
    const month = d.toLocaleDateString('ru-RU', { month: 'long' });
    return `${weekday}, ${day} ${month}`;
  };

  const isOpen = status === 'open';

  // Определяем активный этап (0-3)
  const getActiveMilestone = () => {
    if (progress >= 100) return 3;  // Рыба в пути к вам
    if (progress >= 66) return 2;   // Снаряжаем улов
    if (progress >= 33) return 1;   // Принимаем ваши заказы
    return 0;                       // Нашли лучшие дары моря
  };

  const activeMilestoneIndex = getActiveMilestone();

  return (
    <div className="cycle-status-widget">
      {/* Декоративный фоновый слой с оригами */}
      <div className="widget-decorative-background">
        <img src={origamiCrab} alt="" aria-hidden="true" className="origami-decoration origami-left" loading="lazy" />
        <img src={origamiShrimp} alt="" aria-hidden="true" className="origami-decoration origami-center" loading="lazy" />
        <img src={origamiFish} alt="" aria-hidden="true" className="origami-decoration origami-right" loading="lazy" />
      </div>

      <div className="widget-content">
        {/* Верхняя часть: Заголовок + Таймер */}
        <div className="widget-header">
          <div className="widget-header-left">
            <div className="cycle-headline">
              <span className="cycle-name">{cycleName || (cycleNumber ? `Сбор #${cycleNumber}` : 'Свежий улов')}</span>
              <span className="cycle-slogan">{status === 'inactive' ? 'Ищем лучшие дары моря для вас' : status === 'closed' ? 'Сбор заказов завершён' : 'Сбор заказов открыт — присоединяйтесь'}</span>
            </div>
          </div>

          {/* Таймер (компактный) */}
          {isOpen && closeDate ? (
            <div className="widget-timer">
              <div className="timer-label">Цены действуют:</div>
              <div className="timer-value">
                {timeLeft.days > 0 && `${timeLeft.days}д `}
                {timeLeft.hours}ч {timeLeft.minutes.toString().padStart(2, '0')}м
              </div>
            </div>
          ) : status === 'inactive' ? (
            <div className="widget-timer">
              <div className="timer-value" style={{ fontSize: '14px' }}>Ожидайте</div>
            </div>
          ) : null}
        </div>

        {/* Sentinel: невидимый элемент между header и timeline.
            Когда он уходит за sticky header — таймер появляется в хедере */}
        {timerSentinelRef && <div ref={timerSentinelRef} style={{ height: 0 }} />}

        {/* ====== DESKTOP: Горизонтальный Timeline с water-fill прогрессом ====== */}
        <div
          className="horizontal-timeline-glass"
          style={{
            // Water-fill: прозрачная «вода» слева, матовое стекло справа
            background: (() => {
              const fillPercent = activeMilestoneIndex === 3 ? 100
                : activeMilestoneIndex === 2 ? 75
                : activeMilestoneIndex === 1 ? 50
                : 25;
              return `linear-gradient(90deg,
                rgba(255,255,255,0.08) 0%,
                rgba(255,255,255,0.10) ${fillPercent - 3}%,
                rgba(255,255,255,0.30) ${fillPercent + 3}%,
                rgba(255,255,255,0.28) 100%)`;
            })()
          }}
        >
          {MILESTONES.map((milestone, index) => {
            const Icon = milestone.icon;
            const isActive = progress >= milestone.percent;
            const isCurrent = activeMilestoneIndex === index;
            const stageStyle = getStageColor(isActive, milestone);

            return (
              <div key={milestone.percent} className="h-timeline-stage">
                <div
                  className={`h-timeline-circle ${isActive ? 'active' : ''} ${isCurrent ? 'current' : ''}`}
                  style={{
                    background: stageStyle.background,
                    boxShadow: stageStyle.shadow
                  }}
                >
                  <Icon className="h-timeline-icon-svg" strokeWidth={2.5} />
                </div>
                <span className={`h-timeline-label ${isActive ? 'active' : ''}`}>
                  {milestone.label}
                </span>
              </div>
            );
          })}
        </div>

        {/* ====== MOBILE: Вертикальный Timeline с многослойным glassmorphism ====== */}
        <div className="vertical-timeline-glass">
          {/* Слой 1: Иконки (ПОД стеклом) */}
          <div className="timeline-icons-layer">
            {MILESTONES.map((milestone, index) => {
              const Icon = milestone.icon;
              const isActive = progress >= milestone.percent;
              const isCurrent = activeMilestoneIndex === index;
              const stageStyle = getStageColor(isActive, milestone);

              return (
                <div key={milestone.percent} className="timeline-icon-row">
                  <div
                    className={`timeline-icon ${isActive ? 'active' : ''} ${isCurrent ? 'current' : ''}`}
                    style={{
                      background: stageStyle.background,
                      boxShadow: stageStyle.shadow
                    }}
                  >
                    <Icon className="timeline-icon-svg" strokeWidth={2.5} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Слой 2: Стеклянный слой с water-fill прогрессом (сверху вниз) */}
          <div
            className="timeline-glass-layer"
            style={{
              background: (() => {
                const fillPercent = activeMilestoneIndex === 3 ? 100
                  : activeMilestoneIndex === 2 ? 75
                  : activeMilestoneIndex === 1 ? 50
                  : 25;
                return `linear-gradient(180deg,
                  rgba(255,255,255,0.05) 0%,
                  rgba(255,255,255,0.07) ${fillPercent - 3}%,
                  rgba(255,255,255,0.25) ${fillPercent + 3}%,
                  rgba(255,255,255,0.22) 100%)`;
              })()
            }}
          />

          {/* Слой 3: Текст (НАД стеклом) */}
          <div className="timeline-text-layer">
            {MILESTONES.map((milestone) => {
              const isActive = progress >= milestone.percent;

              return (
                <div key={milestone.percent} className="timeline-text-row">
                  <div className={`timeline-label ${isActive ? 'active' : ''}`}>
                    {milestone.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="widget-footer">
          <div className="cycle-motivating-message">
            Сделайте заказ — цены зафиксированы до конца сбора
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(CycleStatusWidget);
